/* eslint-disable prefer-destructuring */
/* eslint-disable no-param-reassign */
/* eslint-disable no-await-in-loop */
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Container, Grid2, CircularProgress, Typography, Button, Box } from '@mui/material';
import { withStyles } from '@mui/styles';
import { FormProvider, useFieldArray, useForm, Controller } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import _ from 'lodash';
import FlagIcon from '@mui/icons-material/Flag';
import { Alert as MuiAlert } from '@mui/lab';
import { OpenInNew } from '@mui/icons-material';
import ExpenseButtons from '../Components/Expense/ExpenseButtons';
import ExpenseEntryHeader from '../Components/Expense/ExpenseEntryHeader';
import ExpenseReceiptUploadForm from '../Components/Expense/ExpenseReceiptUploadForm';
import {
    createPendingCCExpense,
    updateExpense,
    getExpenseById,
    getAllCreditCards,
    getExpenseCategories,
    getStatusesAllowed,
    createExpense,
} from '../Services/ExpenseService';
import { getDivisions } from '../Services/Common';
import { formatErrorMessage } from '../Utils/ErrorMessage';
import { uploadImages } from '../Utils/UploadImages';
import AddExpenseTab from '../Components/Expense/Tabs/AddExpenseTab';
import ExpenseTabs from '../Components/Expense/Tabs/ExpenseTabs';
import ExpenseTab from '../Components/Expense/Tabs/ExpenseTab';
import ExpenseTabLabel from '../Components/Expense/Tabs/ExpenseTabLabel';
import ExpenseTabPanel from '../Components/Expense/Tabs/ExpenseTabPanel';
import ConfirmationModal from '../Components/Dialog/ConfirmationModal';
import BookingDetailsForm from '../Components/Expense/BookingDetailsForm';
import PreCodedExpenseDetailsForm from '../Components/Expense/PreCodedExpenseDetailsForm';
import Alert from '../Components/Alert';
import ExpenseSummary from '../Components/Expense/ExpenseSummary';
import InternalNotesForm from '../Components/Expense/InternalNotesForm';
import ReasonForSplittingForm from '../Components/Expense/ReasonForSplittingForm';
import AdditionalExpenseDetails from '../Components/Expense/AdditionalExpenseDetails';
import { AdminAuthContext } from '../../Admin/Contexts/AdminAuthContext';
import { ExpenseStatusValuesEnum } from '../Components/Expense/ExpenseStatus.enum';
import { ExpenseClassEnum } from '../Components/Expense/ExpenseClass.enum';
import ExpenseNeedsReviewAlert from '../Components/Expense/ExpenseNeedsReviewAlert';
import { COLORS } from '../../utils/Application_Constants';
import {
    createCorrectionExpenseFromEntries,
    isExpenseViewOnlyByStatus,
    shouldEnableByCorrectionAndApprovedStatus,
    validateAllPostingDatesOnSubmit,
} from '../Utils/ExpenseUtil';
import { ExpenseModeEnum } from '../Components/Expense/ExpenseMode.enum';
import { BillTypeEnum } from '../Components/Expense/BillType.enum';
import AllocationMismatchBox from '../Components/Expense/AllocationMismatchBox';

const styles = (theme) => ({
    ContainerRoot: {
        [theme.breakpoints.down('md')]: {
            paddingLeft: '16px',
            paddingRight: '16px',
        },
        paddingTop: '1.5rem',
        paddingBottom: '5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '40px',
    },
    formGrid2Container: {
        gap: 32,
    },
    summary: {
        position: 'sticky',
        top: '5rem',
        paddingTop: '.5rem',
    },
    flagIcon: {
        cursor: 'pointer',
    },
    viewBookingButton: {
        minWidth: 'auto',
        padding: '4px 8px',
        fontSize: '0.75rem',
    },
    iconContainer: {
        position: 'absolute',
        top: 8,
        right: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        zIndex: 10,
    },
    tabPanel: {
        position: 'relative',
    },
    alertContainer: {
        marginTop: theme.spacing(4),
        [theme.breakpoints.down('sm')]: {
            marginTop: theme.spacing(4),
        },
        paddingLeft: '.5rem',
    },
    leftIconContainer: {
        position: 'absolute',
        top: 8,
        left: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        zIndex: 10,
    },
    editButton: {
        backgroundColor: COLORS.LT_JOURNEY10,
        '&:hover': {
            backgroundColor: COLORS.LT_JOURNEY25,
        },
        color: COLORS.LT_JOURNEY,
    },
});

const defaultPendingExpense = {
    label: 'Expense',
    amount: '',
    billType: '',
    bookingId: null,
    category: '',
    clientId: '',
    clinicianFirstName: '',
    clinicianId: '',
    clinicianLastName: '',
    accountingProcessingNotes: '',
    creditCardId: '',
    currency: 'USD',
    division: '',
    merchantName: '',
    serviceFee: '',
    state: '',
    totalExpense: '',
    type: '',
    status: 'Unassigned',
    salespersonName: '',
    recruiterName: '',
    payDate: null,
    expenseClass: 'Normal',
    splitTransactionComment: '',
    bookingType: '',
    bookingDivision: '',
    bookingCategory: '',
    sgaType: '',
    sgaDivision: '',
    sgaCategory: '',
    allocatedAmount: '',
};

const defaultCCExpense = {
    label: 'Expense',
    amount: '',
    billType: '',
    bookingId: null,
    category: '',
    clientId: '',
    clinicianFirstName: '',
    clinicianId: '',
    clinicianLastName: '',
    accountingProcessingNotes: '',
    creditCardId: '',
    currency: 'USD',
    division: '',
    merchantName: '',
    serviceFee: '',
    state: '',
    totalExpense: '',
    type: '',
    status: ExpenseStatusValuesEnum.New,
    salespersonName: '',
    recruiterName: '',
    payDate: null,
    expenseClass: ExpenseClassEnum.IngestedCCTransaction,
    splitTransactionComment: '',
    bookingType: '',
    bookingDivision: '',
    bookingCategory: '',
    sgaType: '',
    sgaDivision: '',
    sgaCategory: '',
    highPriority: false,
    allocatedAmount: '',
};

const mergeFields = {
    creditCardId: 'Ingested',
    transactionDate: 'Ingested',
    amount: 'Ingested',
    merchantName: 'Ingested',
    division: 'Pending CC/Pre-Code',
    category: 'Pending CC/Pre-Code',
    type: 'Pending CC/Pre-Code',
    startDate: 'Pending CC/Pre-Code',
    endDate: 'Pending CC/Pre-Code',
    bookingId: 'Pending CC/Pre-Code',
    billType: 'Pending CC/Pre-Code',
    clinicianId: 'Pending CC/Pre-Code',
    clinicianFirstName: 'Pending CC/Pre-Code',
    clinicianLastName: 'Pending CC/Pre-Code',
    state: 'Pending CC/Pre-Code',
};

const CreditCardDetails = (props) => {
    const { classes } = props;
    const mode = ExpenseModeEnum.CreditCard;
    const [isLoading, setIsLoading] = useState(false);
    const [divisions, setDivisions] = useState([]);
    const [divisionsSGA, setDivisionsSGA] = useState([]);
    const [statusesAllowed, setStatusesAllowed] = useState([]);
    const { id } = useParams();
    const [alert, setAlert] = useState();
    const [allocationDiff, setAllocationDiff] = useState(0);
    const [showAllocationError, setShowAllocationError] = useState(false);
    const history = useHistory();
    const defaultValues = {
        fileUploads: [{ newFiles: [], existingFiles: [] }],
        expenseEntries: [defaultCCExpense],
    };
    const methods = useForm({ shouldUnregister: false, defaultValues });
    const {
        handleSubmit,
        reset,
        control,
        getValues,
        watch,
        setValue,
        setError,
        clearErrors,
        trigger,
        formState: { errors },
    } = methods;
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const backLink = id ? 'All Credit Card Expenses' : 'Expenses';
    const title = 'CC Expense Entry';
    const expensesPath = id ? '/expenses/credit-card/review' : '/expenses';
    const { isExpenseSystemAdministratorUser, isExpenseAdminUser } = useContext(AdminAuthContext);
    const isViewOnly = !(isExpenseSystemAdministratorUser || isExpenseAdminUser);
    const watchExpenseEntries = watch(`expenseEntries`);
    const [originalExpenseEntries, setOriginalExpenseEntries] = useState([]);
    const [originalPrimaryExpense, setOriginalPrimaryExpense] = useState();
    const [isCorrection, setIsCorrection] = useState(false);
    const isAnyExpensePosted = useMemo(() => {
        if (originalExpenseEntries?.length > 0) {
            return originalExpenseEntries.some(
                (entry) => entry.status === ExpenseStatusValuesEnum.Posted
            );
        }
        return false;
    }, [originalExpenseEntries]);
    const isAllExpensePosted = useMemo(() => {
        if (originalExpenseEntries?.length > 0) {
            return !originalExpenseEntries.some(
                (entry) => entry.status !== ExpenseStatusValuesEnum.Posted
            );
        }
        return false;
    }, [originalExpenseEntries]);
    const [isSGA, setIsSGA] = useState([false, false, false, false, false]);
    const [removeTabs, setRemoveTabs] = useState(false);
    const [expenseCategoriesSGA, setExpenseCategoriesSGA] = useState(null);
    const [expenseCategoriesNonSGA, setExpenseCategoriesNonSGA] = useState(null);
    const [allCreditCards, setallCreditCards] = useState([]);
    const [showBalanceError, setShowBalanceError] = useState(false);
    const hasErrors = !!Object.keys(methods.formState.errors).length;
    const [assignmentsByIndex, setAssignmentsByIndex] = useState({});
    const [openConfirmationEditModal, setOpenConfirmationEditModal] = useState(false);
    const [enableEditByModal, setEnableEditByModal] = useState(false);
    const [enableEditByCorrection, setEnableEditByCorrection] = useState(false);

    const {
        fields: expenseEntries,
        append,
        remove,
    } = useFieldArray({
        control, // control props comes from useForm (optional: if you are using FormContext)
        name: 'expenseEntries', // unique name for your Field Array
        keyName: 'expenseEntryId',
    });

    const activeExpense = useMemo(
        () =>
            Array.isArray(watchExpenseEntries) ? watchExpenseEntries[activeTabIndex] : undefined,
        [watchExpenseEntries, activeTabIndex]
    );

    const { sumAmounts, sumAllocated } = React.useMemo(() => {
        const entries = Array.isArray(watchExpenseEntries) ? watchExpenseEntries : [];
        const sumA = entries.reduce((acc, e) => acc + (parseFloat(e?.amount) || 0), 0);
        const sumAl = entries.reduce((acc, e) => acc + (parseFloat(e?.allocatedAmount) || 0), 0);
        return { sumAmounts: Number(sumA.toFixed(2)), sumAllocated: Number(sumAl.toFixed(2)) };
    }, [watchExpenseEntries]);

    useEffect(() => {
        const currentDiff = Number((sumAmounts - sumAllocated).toFixed(2));
        if (currentDiff === 0 && showAllocationError) {
            setShowAllocationError(false);
            const entries = getValues('expenseEntries') || [];
            entries.forEach((_, idx) => clearErrors(`expenseEntries[${idx}].allocatedAmount`));
        }
    }, [sumAmounts, sumAllocated, showAllocationError, getValues, clearErrors]);

    const handleAssignmentChange = (index, assignment) => {
        setAssignmentsByIndex((prev) => ({
            ...prev,
            [index]: assignment,
        }));
    };

    useEffect(() => {
        setAllocationDiff(Number((sumAmounts - sumAllocated).toFixed(2)));
    }, [sumAmounts, sumAllocated]);

    useEffect(() => {
        const currentExpense = watch(`expenseEntries.${activeTabIndex}`);
        if (currentExpense) {
            setEnableEditByCorrection(shouldEnableByCorrectionAndApprovedStatus(currentExpense));
        }
    }, [watchExpenseEntries, activeTabIndex]);

    const resetImages = (index) => {
        // Resort/Reset Images
        const fileUploads = getValues(`fileUploads`);
        fileUploads.splice(index, 1);
        setValue(`fileUploads`, fileUploads);
    };

    const formatResponse = (res) =>
        Object.fromEntries(
            Object.entries(res).map(([key, value]) => [key, value === null ? '' : value])
        );

    const mergeMatchedValues = (matchedExpense, defaultExpense) =>
        Object.fromEntries(
            Object.entries(mergeFields).map(([key, value]) => {
                let mergedValue = [key];
                const sourceOfTruth = value === 'Ingested' ? matchedExpense : defaultExpense;

                if (
                    matchedExpense[key] &&
                    defaultExpense[key] &&
                    matchedExpense[key] !== defaultExpense[key]
                ) {
                    mergedValue = [...mergedValue, sourceOfTruth[key]];
                } else {
                    mergedValue = [...mergedValue, defaultExpense[key]];
                }

                return mergedValue;
            })
        );

    const fetchExpenseById = async () => {
        try {
            setIsLoading(true);
            // TODO: Get all split/duplicate expense entires instead of singular once API is complete
            const res = await getExpenseById(id);
            const listExpenses = [];
            const aOriginalExpenseEntries = [];
            const aFileUploads = [];

            if (res.primaryExpenseId && res.primaryExpense) {
                // child split
                let childIndex = 0;
                listExpenses.push(res.primaryExpense);
                for (let i = 0; i < res.primaryExpense.splitExpenses.length; i++) {
                    const listExpensesIndex = listExpenses.push(
                        res.primaryExpense.splitExpenses[i]
                    );
                    if (res.primaryExpense.splitExpenses[i].id === id) {
                        childIndex = listExpensesIndex - 1;
                    }
                }

                setActiveTabIndex(childIndex);
            } else {
                listExpenses.push(res);
                for (let i = 0; i < res.splitExpenses.length; i++) {
                    listExpenses.push(res.splitExpenses[i]);
                }
            }

            listExpenses[0].splitExpenses = [];

            for (let i = 0; i < listExpenses.length; i++) {
                // Sets expenseEntries[0] backup defaults
                if (listExpenses[i].expenseClass === ExpenseClassEnum.SalesGeneralAndAdmin) {
                    listExpenses[i].bookingType = '';
                    listExpenses[i].bookingDivision = '';
                    listExpenses[i].bookingCategory = '';
                    listExpenses[i].bookingBillType = '';
                    listExpenses[i].sgaType = listExpenses[i].type;
                    listExpenses[i].sgaDivision = listExpenses[i].division;
                    listExpenses[i].sgaCategory = listExpenses[i].category;
                    listExpenses[i].sgaBillType = listExpenses[i].billType;
                    isSGA[i] = true;
                } else {
                    listExpenses[i].bookingType = listExpenses[i].type;
                    listExpenses[i].bookingDivision = listExpenses[i].division;
                    listExpenses[i].bookingCategory = listExpenses[i].category;
                    listExpenses[i].bookingBillType = listExpenses[i].billType;
                    listExpenses[i].sgaType = '';
                    listExpenses[i].sgaDivision = '';
                    listExpenses[i].sgaCategory = '';
                    listExpenses[i].sgaBillType = '';
                    isSGA[i] = false;
                }
                let formattedValues;

                if (listExpenses[i].matchedId) {
                    const matchedExpense = await getExpenseById(listExpenses[i].matchedId);
                    const matchedValues = mergeMatchedValues(matchedExpense, listExpenses[i]);
                    formattedValues = formatResponse(matchedValues);
                } else {
                    formattedValues = formatResponse(listExpenses[i]);
                }
                // Reset back to Null as empty string on the as the default was causing issues
                formattedValues.payDate =
                    _.isString(formattedValues.payDate) && formattedValues.payDate.length === 0
                        ? null
                        : formattedValues.payDate.substring(0, 10);

                if (
                    formattedValues.allocatedAmount === undefined ||
                    formattedValues.allocatedAmount === null ||
                    formattedValues.allocatedAmount === ''
                ) {
                    formattedValues.allocatedAmount = 0;
                } else {
                    formattedValues.allocatedAmount = Number(formattedValues.allocatedAmount) || 0;
                }

                aOriginalExpenseEntries.push({
                    ...formattedValues,
                    label: listExpenses[i].label ? listExpenses[i].label : `Expense ${i}`,
                });

                if (
                    listExpenses[i] &&
                    listExpenses[i].expenseImages &&
                    listExpenses[i].expenseImages.length >= 0
                ) {
                    aFileUploads.push({
                        newFiles: [],
                        existingFiles: listExpenses[i].expenseImages,
                    });
                } else {
                    aFileUploads.push({ newFiles: [], existingFiles: [] });
                }

                if (!isCorrection && listExpenses[i].expenseVerion > 0) {
                    setIsCorrection(true);
                }
            }

            setIsSGA(isSGA);
            setOriginalExpenseEntries(aOriginalExpenseEntries);
            setOriginalPrimaryExpense(aOriginalExpenseEntries[0]);
            reset({
                fileUploads: aFileUploads,
                expenseEntries: aOriginalExpenseEntries,
            });
        } catch (error) {
            console.error('Error fetching expense by ID:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const clearExpenseHiddenErrors = (preCodedexpense, index) => {
        // Field 1: Transaction Date  transactionDate
        if (!preCodedexpense.category) {
            clearErrors(`expenseEntries[${index}].transactionDate`);
        }

        // Field 2: Start Date  startDate
        // Field 3: End Date  endDate
        if (
            !(
                preCodedexpense.category === 'Travel' &&
                !(
                    preCodedexpense.type === 'Baggage' ||
                    preCodedexpense.type === 'Fuel' ||
                    !preCodedexpense.type
                )
            )
        ) {
            clearErrors(`expenseEntries[${index}].startDate`);
            clearErrors(`expenseEntries[${index}].endDate`);
        }
    };

    const clearAllHiddenErrors = () => {
        const aExpenseEntries = getValues('expenseEntries');
        aExpenseEntries.forEach((element, index) => {
            clearExpenseHiddenErrors(element, index);
        });
    };

    const computeAllocationDiff = () => {
        const entries = getValues('expenseEntries') || [];
        const sumAmounts = entries.reduce((acc, e) => acc + (parseFloat(e?.amount) || 0), 0);
        const sumAllocated = entries.reduce(
            (acc, e) => acc + (parseFloat(e?.allocatedAmount) || 0),
            0
        );
        return Number((sumAmounts - sumAllocated).toFixed(2));
    };

    const prepareExpense = (origExpense) => {
        // Do not modify the existing expense object, clone it
        const expense = JSON.parse(JSON.stringify(origExpense));
        expense.bookingId = parseInt(expense.bookingId, 10);
        expense.clientId = expense.clientId !== '' ? expense.clientId : null;
        expense.serviceFee = expense.serviceFee !== '' ? expense.serviceFee : null;
        expense.creditCardId = parseInt(expense.creditCardId, 10);
        if (expense.billType === BillTypeEnum.ClinicianDeduction) {
            if (expense.category === '') {
                expense.category = null;
            }
            if (expense.type === '') {
                expense.type = null;
            }
            const date = new Date();
            if (expense.transactionDate === '' || expense.transactionDate === null) {
                expense.transactionDate = date.toISOString();
            }
        }
        Object.keys(expense).forEach((i) => {
            if (_.isString(expense[i]) && expense[i].length === 0) {
                expense[i] = null;
            }
        });

        return expense;
    };

    const transformToExpenseAPI = (expenses) => {
        const rtnExpense = expenses[0];

        rtnExpense.splitExpenses = [];
        rtnExpense.expenseImages = [];
        // Copy to splitExpenses
        for (let i = 1; i < expenses.length; i++) {
            const expense = expenses[i];
            expense.expenseImages = [];
            rtnExpense.splitExpenses.push(expense);
        }

        return rtnExpense;
    };

    const onSave = async (data) => {
        try {
            setIsLoading(true);
            let entries = data.expenseEntries;
            if (isCorrection) {
                entries = createCorrectionExpenseFromEntries(data.expenseEntries);
            }
            const expenses = entries.map(prepareExpense);
            const transformExpense = transformToExpenseAPI(expenses);
            const expenseResponse = await updateExpense(transformExpense.id, transformExpense);
            const fileUploads = getValues(`fileUploads`);
            if (!isCorrection) {
                await uploadImages(fileUploads, expenseResponse, mode);
            }

            history.push({
                pathname: '/expenses/submit',
                state: {
                    prevPage: '/expenses/credit-card/review',
                },
            });
        } catch (error) {
            console.error('Error onSaveCC Pending CC Expense Entry');
            reset({ expenseEntries: data.expenseEntries, fileUploads: data.fileUploads });
            const message = formatErrorMessage(error);
            setAlert({
                message,
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Submit will only be used for mode pending
    const onSubmit = async (data) => {
        try {
            setIsLoading(true);
            const expenses = data.expenseEntries.map(prepareExpense);
            const transformExpense = transformToExpenseAPI(expenses);
            const expenseResponse = await createExpense(transformExpense.id, transformExpense);
            const fileUploads = getValues(`fileUploads`);
            await uploadImages(fileUploads, expenseResponse, mode);

            history.push({
                pathname: '/expenses/submit',
                state: { prevPage: '/expenses/pending-cc' },
            });
        } catch (error) {
            console.error('Error onSubmit Pending CC Expense Entry');
            reset({ expenseEntries: data.expenseEntries, fileUploads: data.fileUploads });
            const message = formatErrorMessage(error);
            setAlert({
                message,
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const validateSubmit = async () => {
        if (mode === 'cc') {
            clearAllHiddenErrors();

            await trigger();

            const postingOk = validateAllPostingDatesOnSubmit(
                getValues('expenseEntries'),
                setError,
                clearErrors
            );

            const diff = computeAllocationDiff();
            if (diff !== 0) {
                setAllocationDiff(diff);
                setShowAllocationError(true);

                const entries = getValues('expenseEntries') || [];
                entries.forEach((_, idx) => {
                    setError(`expenseEntries[${idx}].allocatedAmount`, {
                        type: 'manual',
                        message: 'Amounts do not match.',
                    });
                });
            } else {
                const entries = getValues('expenseEntries') || [];
                entries.forEach((_, idx) => clearErrors(`expenseEntries[${idx}].allocatedAmount`));
                setShowAllocationError(false);
            }

            if (!postingOk || computeAllocationDiff() !== 0) return;
        }

        handleSubmit(onSubmit)();
    };

    const validateSave = async () => {
        if (mode === 'cc') {
            clearAllHiddenErrors();

            await trigger();

            const postingOk = validateAllPostingDatesOnSubmit(
                getValues('expenseEntries'),
                setError,
                clearErrors
            );

            const diff = computeAllocationDiff();
            if (diff !== 0) {
                setAllocationDiff(diff);
                setShowAllocationError(true);

                const entries = getValues('expenseEntries') || [];
                entries.forEach((_, idx) => {
                    setError(`expenseEntries[${idx}].allocatedAmount`, {
                        type: 'manual',
                        message: 'Amounts do not match.',
                    });
                });
            } else {
                const entries = getValues('expenseEntries') || [];
                entries.forEach((_, idx) => clearErrors(`expenseEntries[${idx}].allocatedAmount`));
                setShowAllocationError(false);
            }

            if (errors.balance) {
                setError('balance', {
                    type: 'manual',
                    message:
                        'The sum of the expenses does not match the current transaction amount.',
                });
                setShowBalanceError(true);
            } else {
                clearErrors('balance');
                setShowBalanceError(false);
            }

            if (!postingOk || computeAllocationDiff() !== 0 || errors.balance) return;
        }

        handleSubmit(onSave)();
    };

    const handleAddExpense = () => {
        const newExpense = {
            ...getValues('expenseEntries[0]'),
            label: 'New Expense',
            amount: '',
            billType: '',
            allocatedAmount: '',
            id: null,
            created: null,
            lastModified: null,
            status: ExpenseStatusValuesEnum.New,
            highPriority: false,
            salespersonName: '',
            recruiterName: '',
        };

        const newFileUpload = {
            ...getValues('fileUploads[0]'),
        };

        if (newFileUpload) {
            if (newFileUpload.existingFiles && newFileUpload.existingFiles.length > 0) {
                newFileUpload.existingFiles = newFileUpload.existingFiles
                    .filter((file) => !file.isCombinedPdf)
                    .map((file) => ({
                        ...file,
                        expenseId: null, // Clear out the Existing ExpenseId
                    }));
            }

            const cloneNewFiles = [];
            if (newFileUpload.newFiles && newFileUpload.newFiles.length > 0) {
                newFileUpload.newFiles.forEach((file) => {
                    const cloneNewFile = new File([file], file.name, { type: file.type });
                    // Regenerate the preview to make unique for the tab
                    Object.assign(cloneNewFile, {
                        preview: URL.createObjectURL(file),
                    });
                    cloneNewFiles.push(cloneNewFile);
                });
            }
            newFileUpload.newFiles = cloneNewFiles;

            const fileUploads = getValues(`fileUploads`);
            fileUploads.push(newFileUpload);
            setValue(`fileUploads`, fileUploads);
        }

        if (Object.prototype.hasOwnProperty.call(newExpense, 'submittedDate')) {
            newExpense.submittedDate = null;
        }

        if (Object.prototype.hasOwnProperty.call(newExpense, 'resubmittedDate')) {
            newExpense.resubmittedDate = null;
        }

        if (Object.prototype.hasOwnProperty.call(newExpense, 'errors')) {
            delete newExpense.errors;
        }
        if (Object.prototype.hasOwnProperty.call(newExpense, 'statusHistory')) {
            delete newExpense.statusHistory;
        }
        if (Object.prototype.hasOwnProperty.call(newExpense, 'relatedErrors')) {
            delete newExpense.relatedErrors;
        }
        if (Object.prototype.hasOwnProperty.call(newExpense, 'importedData')) {
            delete newExpense.importedData;
        }
        if (!newExpense.splitTransactionComment) {
            newExpense.splitTransactionComment = '';
        }
        if (isSGA[0]) {
            newExpense.sgaType = newExpense.type;
            newExpense.sgaDivision = newExpense.division;
            newExpense.sgaCategory = newExpense.category;
            newExpense.sgaBillType = newExpense.billType;
            newExpense.sgaExpenseClass = newExpense.expenseClass;
        } else {
            newExpense.bookingType = newExpense.type;
            newExpense.bookingDivision = newExpense.division;
            newExpense.bookingCategory = newExpense.category;
            newExpense.bookingBillType = newExpense.billType;
            newExpense.bookingExpenseClass = newExpense.expenseClass;
        }
        isSGA[expenseEntries.length] = isSGA[0];
        setIsSGA(isSGA);
        append(newExpense);
        setActiveTabIndex(expenseEntries.length);
    };

    const resetIsSGA = (index) => {
        // Resort/Reset isSGA flags
        if (index === 4) {
            isSGA[index] = false;
        } else {
            for (let indexLoop = index; indexLoop < isSGA.length; ++indexLoop) {
                if (isSGA[indexLoop + 1] !== undefined) {
                    if (isSGA[indexLoop] !== isSGA[indexLoop + 1]) {
                        isSGA[indexLoop] = isSGA[indexLoop + 1];
                    }
                }
            }
        }
        setIsSGA(isSGA);
    };

    const handleRemoveExpense = (index) => {
        // This was added to address a rendering issue
        // It turns off the TabPanel Display removes the item and then turns back on the display
        setActiveTabIndex(index);
        setRemoveTabs(true);
    };

    useEffect(() => {
        Promise.all([
            getDivisions(true, true),
            getStatusesAllowed(ExpenseClassEnum.IngestedCCTransaction),
            getAllCreditCards(),
            getExpenseCategories('CreditCard'),
            getExpenseCategories('SGA'),
        ])
            .then(async ([divsSGA, statuses, cc, catsNonSGA, catsSGA]) => {
                setDivisions(divsSGA.filter((val) => val.isSGA === false));
                setDivisionsSGA(divsSGA);
                setallCreditCards(cc);
                setExpenseCategoriesNonSGA(catsNonSGA);
                setExpenseCategoriesSGA(catsSGA);
                setStatusesAllowed(statuses);
            })
            .catch((error) => {
                console.error(error);
            });
        if (id) {
            fetchExpenseById();
        }
    }, []);

    // This was added to address a rendering issue
    // It turns off the TabPanel Display removes the item and then turns back on the display
    useEffect(() => {
        if (removeTabs) {
            resetIsSGA(activeTabIndex);
            resetImages(activeTabIndex);
            remove(activeTabIndex);
            setActiveTabIndex(activeTabIndex - 1);
            setRemoveTabs(false);
        }
    }, [removeTabs]);

    useEffect(() => {
        setEnableEditByModal(false);
    }, [activeTabIndex]);

    const enableCorrectionEditMode = () => {
        setOpenConfirmationEditModal(false);
        setEnableEditByModal(true);
        const currentVersion = getValues(`expenseEntries.${activeTabIndex}.expenseVersion`);
        const updatedVersion = currentVersion ? currentVersion + 1 : 1;
        setIsCorrection(true);
        setValue(`expenseEntries.${activeTabIndex}.expenseVersion`, updatedVersion);
    };

    return (
        <Container maxWidth="xl" className={classes.ContainerRoot}>
            <FormProvider {...methods}>
                <Grid2 container direction="column" spacing={2}>
                    <ExpenseEntryHeader
                        title={title}
                        backLink={backLink}
                        expensesPath={expensesPath}
                    />
                    {isLoading ? (
                        <CircularProgress color="primary" />
                    ) : (
                        <>
                            <Grid2 item size={{ xs: 12 }}>
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <Grid2 container spacing={4}>
                                        <Grid2 item size={{ sm: 7, md: 8, lg: 9, xs: 12 }}>
                                            <ExpenseTabs value={activeTabIndex}>
                                                {watchExpenseEntries.map((expense, index) => (
                                                    <ExpenseTab
                                                        key={expense.expenseEntryId}
                                                        id={`expense-tab-${index}`}
                                                        label={
                                                            <ExpenseTabLabel
                                                                isActive={activeTabIndex === index}
                                                                index={index}
                                                                handleRemoveTab={() =>
                                                                    handleRemoveExpense(index)
                                                                }
                                                                setValue={setActiveTabIndex}
                                                                tab={expense}
                                                                mode={mode}
                                                                isViewOnly={isViewOnly}
                                                                originalPrimaryExpense={
                                                                    originalPrimaryExpense
                                                                }
                                                                isAnyExpensePosted={
                                                                    isAnyExpensePosted
                                                                }
                                                            />
                                                        }
                                                    />
                                                ))}
                                                {!isViewOnly && !isAnyExpensePosted && (
                                                    <AddExpenseTab
                                                        label={<AddIcon color="primary" />}
                                                        disabled={
                                                            watchExpenseEntries.length === 5 ||
                                                            isExpenseViewOnlyByStatus(
                                                                originalExpenseEntries?.[
                                                                    activeTabIndex
                                                                ]
                                                            )
                                                        }
                                                        onClick={handleAddExpense}
                                                        id="expense-tab-add"
                                                    />
                                                )}
                                            </ExpenseTabs>
                                            {!removeTabs && (
                                                <div>
                                                    {watchExpenseEntries.map((expense, index) => (
                                                        <ExpenseTabPanel
                                                            className={classes.tabPanel}
                                                            key={`${expense.id}-panel`}
                                                            value={activeTabIndex}
                                                            index={index}
                                                            id={`expense-panel-${index}`}
                                                        >
                                                            <Box
                                                                className={
                                                                    classes.leftIconContainer
                                                                }
                                                            >
                                                                {isExpenseViewOnlyByStatus(
                                                                    originalExpenseEntries?.[index]
                                                                ) &&
                                                                    !enableEditByModal && (
                                                                        <Button
                                                                            variant="contained"
                                                                            className={
                                                                                classes.editButton
                                                                            }
                                                                            color="primary"
                                                                            onClick={() =>
                                                                                setOpenConfirmationEditModal(
                                                                                    true
                                                                                )
                                                                            }
                                                                        >
                                                                            Edit
                                                                        </Button>
                                                                    )}
                                                            </Box>
                                                            <Box className={classes.iconContainer}>
                                                                {assignmentsByIndex[index] && (
                                                                    <Button
                                                                        variant="outlined"
                                                                        color="primary"
                                                                        href={
                                                                            assignmentsByIndex[
                                                                                index
                                                                            ].sfLink
                                                                        }
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        endIcon={<OpenInNew />}
                                                                        className={
                                                                            classes.viewBookingButton
                                                                        }
                                                                    >
                                                                        View booking
                                                                    </Button>
                                                                )}
                                                                <Controller
                                                                    name={`expenseEntries[${index}].highPriority`}
                                                                    control={control}
                                                                    defaultValue={
                                                                        expense.highPriority
                                                                    }
                                                                    render={({
                                                                        onChange,
                                                                        value,
                                                                    }) => (
                                                                        <FlagIcon
                                                                            fontSize="large"
                                                                            className={
                                                                                classes.flagIcon
                                                                            }
                                                                            onClick={() =>
                                                                                onChange(!value)
                                                                            }
                                                                            style={{
                                                                                color: value
                                                                                    ? COLORS.LT_PEPPER
                                                                                    : COLORS.LT_MIDNIGHT25,
                                                                            }}
                                                                        />
                                                                    )}
                                                                />
                                                            </Box>
                                                            <div className={classes.alertContainer}>
                                                                {expense.errors &&
                                                                    expense.errors.length > 0 && (
                                                                        <ExpenseNeedsReviewAlert
                                                                            expense={expense}
                                                                        />
                                                                    )}
                                                            </div>
                                                            <Grid2
                                                                container
                                                                direction="column"
                                                                className={
                                                                    classes.formGrid2Container
                                                                }
                                                            >
                                                                <Grid2 item size={{ xs: 12 }}>
                                                                    {!isSGA[index] && (
                                                                        <BookingDetailsForm
                                                                            index={index}
                                                                            expense={expense}
                                                                            divisions={divisions}
                                                                            setIsSGA={setIsSGA}
                                                                            originalExpenseEntries={
                                                                                originalExpenseEntries
                                                                            }
                                                                            isSGA={isSGA}
                                                                            onAssignmentChange={
                                                                                handleAssignmentChange
                                                                            }
                                                                            isViewOnly={
                                                                                isViewOnly ||
                                                                                (isExpenseViewOnlyByStatus(
                                                                                    originalExpenseEntries?.[
                                                                                        index
                                                                                    ]
                                                                                ) &&
                                                                                    !enableEditByModal)
                                                                            }
                                                                            enableEditByCorrection={
                                                                                enableEditByCorrection
                                                                            }
                                                                        />
                                                                    )}
                                                                </Grid2>
                                                                <Grid2 item size={{ xs: 12 }}>
                                                                    <PreCodedExpenseDetailsForm
                                                                        index={index}
                                                                        expense={expense}
                                                                        divisions={
                                                                            isSGA[index]
                                                                                ? divisionsSGA
                                                                                : divisions
                                                                        }
                                                                        setIsSGA={setIsSGA}
                                                                        isSGA={isSGA}
                                                                        expenseCategoriesSGA={
                                                                            expenseCategoriesSGA
                                                                        }
                                                                        expenseCategoriesNonSGA={
                                                                            expenseCategoriesNonSGA
                                                                        }
                                                                        allCreditCards={
                                                                            allCreditCards
                                                                        }
                                                                        isViewOnly={
                                                                            isViewOnly ||
                                                                            (isExpenseViewOnlyByStatus(
                                                                                originalExpenseEntries?.[
                                                                                    index
                                                                                ]
                                                                            ) &&
                                                                                !enableEditByModal &&
                                                                                !enableEditByCorrection)
                                                                        }
                                                                        isCreditCardTransaction={
                                                                            true
                                                                        }
                                                                    />
                                                                </Grid2>
                                                                {id &&
                                                                    (isExpenseSystemAdministratorUser ||
                                                                        isExpenseAdminUser) &&
                                                                    expense.importedData &&
                                                                    Object.keys(
                                                                        expense.importedData
                                                                    ).length > 0 && (
                                                                        <Grid2
                                                                            item
                                                                            size={{ xs: 12 }}
                                                                        >
                                                                            <AdditionalExpenseDetails
                                                                                index={index}
                                                                                expense={expense}
                                                                                mode={mode}
                                                                                isViewOnly={
                                                                                    isViewOnly ||
                                                                                    (isExpenseViewOnlyByStatus(
                                                                                        originalExpenseEntries?.[
                                                                                            index
                                                                                        ]
                                                                                    ) &&
                                                                                        !enableEditByModal &&
                                                                                        !enableEditByCorrection)
                                                                                }
                                                                            />
                                                                        </Grid2>
                                                                    )}
                                                                <Grid2 item size={{ xs: 12 }}>
                                                                    <ExpenseReceiptUploadForm
                                                                        index={index}
                                                                        expense={expense}
                                                                        originalExpenseEntries={
                                                                            originalExpenseEntries
                                                                        }
                                                                        mode={mode}
                                                                        isViewOnly={
                                                                            isViewOnly ||
                                                                            (isExpenseViewOnlyByStatus(
                                                                                originalExpenseEntries?.[
                                                                                    index
                                                                                ]
                                                                            ) &&
                                                                                !enableEditByModal &&
                                                                                !enableEditByCorrection)
                                                                        }
                                                                    />
                                                                </Grid2>
                                                                <Grid2 item size={{ xs: 12 }}>
                                                                    <InternalNotesForm
                                                                        index={index}
                                                                        expense={expense}
                                                                        mode={mode}
                                                                        originalExpenseEntries={
                                                                            originalExpenseEntries
                                                                        }
                                                                        statusesAllowed={
                                                                            statusesAllowed
                                                                        }
                                                                        isReimbursement={false}
                                                                        isViewOnly={
                                                                            isViewOnly ||
                                                                            (isExpenseViewOnlyByStatus(
                                                                                originalExpenseEntries?.[
                                                                                    index
                                                                                ]
                                                                            ) &&
                                                                                !enableEditByModal &&
                                                                                !enableEditByCorrection)
                                                                        }
                                                                    />
                                                                </Grid2>
                                                                <Grid2 item size={{ xs: 12 }}>
                                                                    <ReasonForSplittingForm
                                                                        index={index}
                                                                        expense={expense}
                                                                        originalExpenseEntries={
                                                                            originalExpenseEntries
                                                                        }
                                                                        mode={mode}
                                                                        isViewOnly={
                                                                            isViewOnly ||
                                                                            (isExpenseViewOnlyByStatus(
                                                                                originalExpenseEntries?.[
                                                                                    index
                                                                                ]
                                                                            ) &&
                                                                                !enableEditByModal &&
                                                                                !enableEditByCorrection)
                                                                        }
                                                                    />
                                                                </Grid2>
                                                            </Grid2>
                                                        </ExpenseTabPanel>
                                                    ))}
                                                </div>
                                            )}
                                        </Grid2>
                                        <Grid2 item size={{ md: 4, sm: 5, lg: 3, xs: 12 }}>
                                            <div className={classes.summary}>
                                                <ExpenseSummary
                                                    mode={mode}
                                                    setError={setError}
                                                    clearErrors={clearErrors}
                                                    setShowBalanceError={setShowBalanceError}
                                                />
                                                {showBalanceError && (
                                                    <Typography color="error">
                                                        {errors.balance.message}
                                                    </Typography>
                                                )}
                                                {showAllocationError && (
                                                    <AllocationMismatchBox
                                                        chargeAmount={sumAmounts}
                                                        allocatedAmount={sumAllocated}
                                                        difference={allocationDiff}
                                                        currency={activeExpense?.currency || 'USD'}
                                                    />
                                                )}
                                            </div>
                                        </Grid2>
                                    </Grid2>
                                </form>
                            </Grid2>
                            {hasErrors > 0 && (
                                <Grid2 container className={classes.alertContainer}>
                                    <Grid2 item size={{ xs: 12, sm: 7, md: 8, lg: 9 }}>
                                        <MuiAlert severity="error">
                                            Please review the highlighted fields above and correct
                                            the errors to submit your expense.
                                        </MuiAlert>
                                    </Grid2>
                                </Grid2>
                            )}
                            {((!isViewOnly && !isAllExpensePosted) || enableEditByModal) && (
                                <Grid2 item size={{ xs: 12 }}>
                                    <ExpenseButtons
                                        validateSubmit={validateSubmit}
                                        validateSave={validateSave}
                                        expensesPath={
                                            id ? '/expenses/credit-card/review' : '/expenses'
                                        }
                                        isViewOnly={isExpenseViewOnlyByStatus(
                                            originalExpenseEntries[activeTabIndex]
                                        )}
                                        enableEditByModal={enableEditByModal}
                                    />
                                </Grid2>
                            )}
                        </>
                    )}
                </Grid2>
            </FormProvider>
            <Alert alert={alert} setAlert={setAlert} />
            <ConfirmationModal
                open={openConfirmationEditModal}
                text="You are attempting to edit an approved expense, do you want to continue?"
                onYes={enableCorrectionEditMode}
                onNo={() => {
                    setOpenConfirmationEditModal(false);
                    setEnableEditByModal(false);
                }}
            />
        </Container>
    );
};

export default withStyles(styles)(CreditCardDetails);
